import { useCallback, useEffect, useRef, useState } from 'react';
import type { BusyRetryState } from './busy-retry';

/** Where a busy-retried action is: a request in flight, the wait before a retry, or neither. */
export type BusyPhase = 'idle' | 'in-flight' | 'waiting';

/** What one run hands to `runWithBusyRetry`. */
export interface BusyRunHandles {
  signal: AbortSignal;
  onAttempt: () => void;
  onRetry: (state: BusyRetryState) => void;
}

/**
 * The lifecycle a flow needs around `runWithBusyRetry` (R8), shared by login,
 * start and submit so they cannot drift apart. Each flow keeps only its own
 * outcome mapping.
 *
 * - `begin()` starts a run: it aborts any previous one, is `in-flight` at once
 *   (before the request is sent), and returns the signal and callbacks for the
 *   runner. The callbacks are bound to their run: once it is aborted they do
 *   nothing, so a late one cannot write over the run that replaced it. Leaving
 *   the page aborts the run, so no wait or request outlives it.
 * - `retry` is the latest busy answer's retry, from the first one until
 *   `settle()`: the busy label and status line stay up for the whole sequence
 *   instead of flickering on every attempt.
 * - `settle(refocus)` ends the run; with `refocus` the hook calls `focus` once
 *   the re-enabled controls have rendered.
 * - `close(onStopped)` is every way out of a dialog that started the run (M4).
 *   It is refused while a request is in flight: the request may commit, and
 *   closing would hide the outcome. During the wait before a retry it aborts the
 *   run, settles it and calls `onStopped`, where the flow refreshes what an
 *   attempt may have changed; no toast, since the person chose to stop. It
 *   reads the phase from a ref, so a handler bound in an earlier render still
 *   sees the current one.
 * - `isRunning()` is the same ref read, for a handler that outlives its render
 *   (a toast action) and must not start over a run already under way.
 */
export function useBusyRun(focus?: () => void) {
  const [phase, setPhaseState] = useState<BusyPhase>('idle');
  const [retry, setRetry] = useState<{ n: number; of: number } | null>(null);
  const [refocus, setRefocus] = useState(0);
  const phaseRef = useRef<BusyPhase>('idle');
  const controllerRef = useRef<AbortController | null>(null);
  const focusRef = useRef(focus);
  focusRef.current = focus;

  const setPhase = useCallback((next: BusyPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  useEffect(() => () => controllerRef.current?.abort(), []);
  useEffect(() => {
    if (refocus > 0) focusRef.current?.();
  }, [refocus]);

  const begin = useCallback((): BusyRunHandles => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setRetry(null);
    setPhase('in-flight');
    return {
      signal: controller.signal,
      onAttempt: () => {
        if (!controller.signal.aborted) setPhase('in-flight');
      },
      onRetry: ({ retry: n, maxRetries }) => {
        if (controller.signal.aborted) return;
        setPhase('waiting');
        setRetry({ n, of: maxRetries });
      },
    };
  }, [setPhase]);

  const settle = useCallback(
    (thenRefocus = false) => {
      setPhase('idle');
      setRetry(null);
      if (thenRefocus) setRefocus((n) => n + 1);
    },
    [setPhase],
  );

  const close = useCallback(
    (onStopped: () => void): boolean => {
      if (phaseRef.current === 'in-flight') return false;
      if (phaseRef.current === 'waiting') {
        controllerRef.current?.abort();
        settle();
        onStopped();
      }
      return true;
    },
    [settle],
  );

  /**
   * A follow-up request of the same run (submit's re-read) is in flight. It is
   * not a retry, so the retry state comes down: no counter and no "we'll keep
   * trying" while it runs.
   */
  const markInFlight = useCallback(() => {
    setPhase('in-flight');
    setRetry(null);
  }, [setPhase]);

  const isRunning = useCallback(() => phaseRef.current !== 'idle', []);

  return { phase, retry, begin, settle, close, markInFlight, isRunning };
}
